import { useRef, useState } from 'react';

import { Icon } from '@dcc-erp/ui';

import { useStagePettyCashReceipt } from '../../lib/queries/petty-cash';

export interface ReceiptDropzoneProps {
  companyId: string | undefined;
  /** Called with the staged attachmentId + filename on success, or ('','') on remove. */
  onStaged: (attachmentId: string, filename: string) => void;
  /** Filename of the currently-staged receipt, or null when nothing is staged. */
  stagedName: string | null;
}

const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';

// Minimal receipt dropzone for the hand-rolled petty-cash entry modal. Inline
// styles mirror the modal's other fields (it does NOT use AppModal). Stages the
// picked file via useStagePettyCashReceipt → reports the attachmentId up so the
// entry submit can carry it (server hard-blocks entries without a receipt or the
// no-doc flag). Deliberately does NOT depend on the doc-ingest upload hook.
export function ReceiptDropzone({ companyId, onStaged, stagedName }: ReceiptDropzoneProps) {
  const stage = useStagePettyCashReceipt(companyId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(file: File) {
    setError(null);
    setBusy(true);
    try {
      const res = await stage.mutateAsync(file);
      onStaged(res.attachmentId, file.name);
    } catch (e) {
      const detail = e instanceof Error ? e.message : 'เกิดข้อผิดพลาด';
      setError(`แนบไฟล์ไม่สำเร็จ: ${detail}`);
    } finally {
      setBusy(false);
    }
  }

  const disabled = !companyId || busy;

  // Staged state — show the filename + a remove button instead of the dropzone.
  if (stagedName) {
    return (
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '12px 14px',
          background: 'var(--positive-bg, #f0fdf4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 14,
            color: 'var(--positive)',
            minWidth: 0,
          }}
        >
          <Icon name="check" size={16} />
          <span
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={stagedName}
          >
            แนบแล้ว: {stagedName}
          </span>
        </span>
        <button
          type="button"
          className="btn sm ghost"
          onClick={() => onStaged('', '')}
          style={{ flexShrink: 0 }}
        >
          ลบ
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
      />
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (disabled) return;
          const f = e.dataTransfer.files?.[0];
          if (f) void handleFile(f);
        }}
        style={{
          border: `2px dashed ${dragOver ? 'var(--brand)' : 'var(--border-strong)'}`,
          borderRadius: 8,
          padding: '18px',
          textAlign: 'center',
          background: dragOver ? 'var(--brand-bg, #f5f8ff)' : '#fafafa',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 6,
            color: 'var(--text-faint)',
          }}
        >
          <Icon name="upload" size={22} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>ลากไฟล์มาวางที่นี่ หรือ</div>
        <button
          type="button"
          className="btn primary"
          style={{ marginTop: 8 }}
          disabled={disabled}
          onClick={() => fileRef.current?.click()}
        >
          <Icon name="upload" size={14} /> เลือกไฟล์
        </button>
        <div className="meta" style={{ marginTop: 6 }}>
          รองรับ JPG, PNG, WEBP, PDF
        </div>
        {busy && (
          <div className="muted" style={{ marginTop: 8, fontSize: 13 }} aria-live="polite">
            กำลังอัปโหลด…
          </div>
        )}
        {error && (
          <div
            role="alert"
            style={{
              marginTop: 8,
              color: 'var(--negative)',
              background: 'var(--negative-bg)',
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
